// S2-LIB4: Histogram
// A horizontal histogram of bucketed counts with animated bar reveal.
// Mirrors the `.hist-*` classes in docs/sprint-2/wireframes/s2-cc6-hero-and-section3.html.
//
// Why a custom component instead of Recharts:
//  - Exact token-driven visuals (no inline-style leakage, no hardcoded colors).
//  - The LLM Control Center only needs a simple binned bar chart — a full
//    chart lib would add kb with no benefit.
//  - Empty and all-zero states have different UX requirements that are
//    awkward to express with Recharts.
//
// Pattern mirrors DonutChart: pre-compute geometry once per buckets change,
// then animate widths from 0 -> final via a useEffect + requestAnimationFrame
// so CSS transitions pick up the width change on the next frame.
import { useEffect, useMemo, useRef, useState } from 'react';
import './Histogram.css';

/** A single bar in the histogram. `label` is the bucket name (e.g. "0–5k")
 *  and `count` is the raw count — the component normalizes widths to maxCount. */
export interface HistogramBucket {
  label: string;
  count: number;
}

export interface HistogramProps {
  buckets: HistogramBucket[];
  /** Optional caption shown right-aligned below the bars (e.g. "peak: 35.4k tokens"). */
  caption?: string;
  /** Reserved for future; only 'horizontal' is supported today. */
  orientation?: 'horizontal';
  /** CSS color for the bar fill. Default var(--primary-500).
   *  Opacity (0.85) comes from CSS so the token stays the single source. */
  barColor?: string;
  /** Message shown when buckets is empty OR all counts are 0. */
  emptyMessage?: string;
}

// Number formatter for bar counts. >= 1000 -> "1.2k" (1 decimal); else the raw int.
// Declared module-scope so we don't reallocate on every render.
const kFormatter = new Intl.NumberFormat('en', {
  maximumFractionDigits: 1,
  minimumFractionDigits: 1,
});

/** Format a count for the right cell. Use "Nk" above 1000, plain int otherwise. */
function formatCount(count: number): string {
  if (count >= 1000) {
    return `${kFormatter.format(count / 1000)}k`;
  }
  return String(count);
}

export function Histogram({
  buckets,
  caption,
  // orientation is accepted but ignored; reserved for future vertical mode.
  orientation: _orientation = 'horizontal',
  barColor,
  emptyMessage = 'No data yet',
}: HistogramProps) {
  // ---- Derived state --------------------------------------------------
  // Pure-empty: no buckets at all -> render a single centered message.
  const isPureEmpty = buckets.length === 0;
  // All-zero: we DO render rows (so the labels + empty tracks still appear)
  // but overlay the emptyMessage on top.
  const isAllZero =
    !isPureEmpty && buckets.every((b) => (b.count || 0) === 0);
  // maxCount drives the bar-width percentage. Guard against 0 to avoid NaN.
  const maxCount = useMemo(() => {
    const m = buckets.reduce((acc, b) => Math.max(acc, b.count || 0), 0);
    return m > 0 ? m : 1;
  }, [buckets]);

  // ---- Animation ------------------------------------------------------
  // Mirror DonutChart's approach: key the animation on a hash of the bucket
  // labels so any prop change triggers a replay from width 0. On mount we
  // render widths=0, then requestAnimationFrame -> flip to `animated=true`
  // so CSS transitions animate to the real widths.
  const bucketsKey = useMemo(
    () => buckets.map((b) => `${b.label}:${b.count}`).join('|'),
    [buckets]
  );
  const [animated, setAnimated] = useState(false);
  const prevKeyRef = useRef<string>('');

  useEffect(() => {
    if (prevKeyRef.current !== bucketsKey) {
      prevKeyRef.current = bucketsKey;
      setAnimated(false);
      // One frame later, flip to animated=true so the CSS transition fires.
      const raf = requestAnimationFrame(() => setAnimated(true));
      return () => cancelAnimationFrame(raf);
    }
  }, [bucketsKey]);

  // ---- Render ---------------------------------------------------------
  // Pure-empty: just the centered empty block — no rows at all.
  if (isPureEmpty) {
    return (
      <div className="airgent-histogram">
        <div className="airgent-histogram-empty">{emptyMessage}</div>
      </div>
    );
  }

  return (
    <div className="airgent-histogram">
      {/* Bars container — we wrap rows so the all-zero overlay can absolutely
          position over them without breaking row layout. */}
      <div
        className={
          'airgent-histogram-rows' + (isAllZero ? ' is-all-zero' : '')
        }
      >
        {buckets.map((b, i) => {
          const count = b.count || 0;
          // Pre-animation -> 0%. Post-animation -> real %.
          const widthPct = animated ? (count / maxCount) * 100 : 0;
          // Zero-count buckets render no fill div (cleaner DOM + matches spec).
          const showFill = count > 0;
          return (
            <div
              key={`${b.label}-${i}`}
              className="airgent-histogram-row"
              data-testid="histogram-row"
            >
              <span className="airgent-histogram-bucket">{b.label}</span>
              <div
                className="airgent-histogram-track"
                data-testid="histogram-track"
              >
                {showFill && (
                  <div
                    className="airgent-histogram-fill"
                    data-testid="histogram-fill"
                    style={{
                      width: `${widthPct}%`,
                      // Inline backgroundColor so caller override wins; fallback
                      // to the token so the CSS file stays color-free.
                      backgroundColor: barColor ?? 'var(--primary-500)',
                    }}
                  />
                )}
              </div>
              <span className="airgent-histogram-count">
                {formatCount(count)}
              </span>
            </div>
          );
        })}

        {/* All-zero overlay — centered emptyMessage that doesn't affect row
            heights (position: absolute, inset: 0). */}
        {isAllZero && (
          <div
            className="airgent-histogram-empty-overlay"
            data-testid="histogram-empty-overlay"
          >
            {emptyMessage}
          </div>
        )}
      </div>

      {/* Caption — only rendered when provided; right-aligned below bars. */}
      {caption && (
        <div
          className="airgent-histogram-caption"
          data-testid="histogram-caption"
        >
          {caption}
        </div>
      )}
    </div>
  );
}
